import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import * as styles from '../offices.styles';
import * as workspaceStyles from '../office-workspace.styles';
import * as surfaceStyles from '../../../core/admin-surface.styles';
import type DepartmentChangeData from '../models/department/department-change-data';
import Department from '../models/department/department';
import useDepartments from '../hooks/departments.hook';
import CreateDepartmentModal from '../modals/create-department-modal.component';
import DepartmentCard from '../cards/department-card.component';
import { createDepartment, deleteDepartment, updateDepartment } from '../services/department.services';
import type Room from '../models/room/room';
import DepartmentForOfficeRoom from '../models/department/department-for-office-room';
import ActivityIndicator from '../../../core/components/activity-indicator/activity-indicator.component';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import { Center } from '../../../core/components/center/center.component';
import notify from '../../../core/components/notify/notify.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import StyleConstants from '../../../core/styles.constants';
import stringIsNullOrWhitespace from '../../../core/utils/validation-utils';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';

interface Props {
  readonly officeId: string | undefined;
  readonly rooms: Room[];
  /** Lets the rooms card group/filter by department without fetching the list a second time. */
  readonly onDepartmentsChange?: (departments: Department[]) => void;
}

const OfficeDepartmentsGroup = ({ officeId, rooms, onDepartmentsChange }: Props): React.JSX.Element => {
  const { t } = useTranslation();

  const isMobile = useContext(IsMobileContext);
  const [showCreateDepartmentModal, setShowCreateDepartmentModal] = useState(false);

  const [isLoading, setIsLoading] = useState(true);

  const keycloak = useKeycloak();

  const [departmentsToShow, setDepartmentsToShow] = useState<Department[] | undefined>(undefined);
  const { departments } = useDepartments({ id: officeId });

  // Adopts whatever the hook last fetched — see the matching note in
  // OfficeRoomsGroup. The `departmentsToShow == null` guard this replaces took
  // the local copy once and never refreshed it, so a new office's departments
  // arrived and were ignored.
  useEffect(() => {
    if (departments != null && rooms) {
      setIsLoading(false);
      setDepartmentsToShow([...departments]);
    }
  }, [departments, rooms]);

  // Held in a ref and kept out of the dependency array on purpose: a caller passing an inline
  // callback would otherwise re-run this effect on every render, write parent state, and loop.
  const onDepartmentsChangeRef = useRef(onDepartmentsChange);
  onDepartmentsChangeRef.current = onDepartmentsChange;

  useEffect(() => {
    if (departmentsToShow != null) {
      onDepartmentsChangeRef.current?.(departmentsToShow);
    }
  }, [departmentsToShow]);

  const handleShowCreate = (): void => {
    setShowCreateDepartmentModal(true);
  };

  const handleClose = useCallback(() => {
    setShowCreateDepartmentModal(false);
  }, []);

  const isDepartmentEmptyOrAlreadyAdded = useCallback(
    (departmentChangeData: DepartmentChangeData) => {
      if (stringIsNullOrWhitespace(departmentChangeData.department.name)) {
        notify(t('name-empty-message'), false);
        return true;
      }

      if (departmentsToShow != null) {
        if (
          departmentsToShow.some(
            (oldDepartment) =>
              oldDepartment.name === departmentChangeData.department.name && oldDepartment.id !== departmentChangeData.department.id,
          )
        ) {
          notify(t('department-already-exists'), false);
          return true;
        }
      }

      return false;
    },
    [departmentsToShow, t],
  );

  const handleUpdateDepartment = useCallback(
    async (departmentChangeData: DepartmentChangeData): Promise<void> => {
      if (isDepartmentEmptyOrAlreadyAdded(departmentChangeData)) return;
      if (!officeId) return;

      const result = await updateDepartment(officeId, departmentChangeData, keycloak);

      if (result != null) {
        let departmentsCopy = departmentsToShow != null ? [...departmentsToShow] : [];

        const existingDepartmentToUpdate = departmentsCopy?.find((department) => department.id === departmentChangeData.department.id);

        departmentsCopy = departmentsCopy.filter((department) => department !== existingDepartmentToUpdate);

        if (existingDepartmentToUpdate != null) {
          const newDepartment = new Department();
          newDepartment.id = departmentChangeData.department.id;
          newDepartment.name = departmentChangeData.department.name;
          newDepartment.rooms = departmentChangeData.rooms;

          departmentsCopy.push(newDepartment);
        }

        setDepartmentsToShow(departmentsCopy.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)));
      }
    },
    [isDepartmentEmptyOrAlreadyAdded, departmentsToShow, officeId],
  );

  const handleDeleteDepartment = useCallback(
    async (departmentChangeData: DepartmentChangeData): Promise<void> => {
      if (!departmentChangeData.department.id) return;

      const result = await deleteDepartment(departmentChangeData.department.id, keycloak);

      if (result) {
        const existingDepartmentToDelete = departmentsToShow?.find((department) => department.id === departmentChangeData.department.id);

        if (existingDepartmentToDelete != null) {
          let departmentsCopy = departmentsToShow != null ? [...departmentsToShow] : [];

          departmentsCopy = departmentsCopy.filter((department) => department !== existingDepartmentToDelete);

          setDepartmentsToShow(departmentsCopy);
        }
      }
    },
    [departmentsToShow],
  );

  const handleCreateDepartment = useCallback(
    async (departmentChangeData: DepartmentChangeData): Promise<void> => {
      if (isDepartmentEmptyOrAlreadyAdded(departmentChangeData)) return;
      if (!officeId) return;

      const result = await createDepartment(officeId, departmentChangeData, keycloak);

      if (result) {
        const resultData = JSON.parse(JSON.stringify(result));
        let department: Department = new Department();
        department = departmentChangeData.department;
        department.id = resultData.id;

        const roomsData: DepartmentForOfficeRoom[] = [];
        departmentChangeData.rooms.forEach((room) => {
          const roomData: DepartmentForOfficeRoom = new DepartmentForOfficeRoom();
          roomData.id = room.id;
          roomData.name = room.name;

          roomsData.push(roomData);
        });

        department.rooms = roomsData;

        const departmentsCopy = departmentsToShow != null ? [...departmentsToShow] : [];
        departmentsCopy.push(department);

        setDepartmentsToShow(departmentsCopy.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)));
      }
      handleClose();
    },
    [handleClose, isDepartmentEmptyOrAlreadyAdded, departmentsToShow, officeId],
  );

  return (
    <div className={style(surfaceStyles.card)}>
      <div className={style(surfaceStyles.cardHead(isMobile, true))}>
        <div>
          <h2 className={style(surfaceStyles.cardTitle)}>{t('departments')}</h2>
          <p className={style(surfaceStyles.cardSub)}>{t('departments-card-subtitle')}</p>
        </div>
        <PrimaryButton fullWidth onClick={handleShowCreate} disabled={isLoading}>
          {t('create-new-department')}
        </PrimaryButton>
      </div>
      {((): React.JSX.Element => {
        if (isLoading) {
          return (
            <div className={style(workspaceStyles.cardBody)}>
              <Center>
                <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
              </Center>
            </div>
          );
        }

        if (departmentsToShow === undefined || departmentsToShow.length === 0) {
          return (
            <div className={style(workspaceStyles.cardBody)}>
              <span className={style(styles.emptyListMessage)}>{t('no-existing-departments')}</span>
            </div>
          );
        }

        return (
          <div>
            {departmentsToShow.map((department, index) => (
              <DepartmentCard
                key={`${department.name} ${department.id ?? ''}`}
                accentIndex={index}
                initialDepartment={department}
                rooms={rooms ?? []}
                handleChange={(departmentChangeData): void => {
                  handleUpdateDepartment(departmentChangeData);
                }}
                isNameEmptyOrInUse={isDepartmentEmptyOrAlreadyAdded}
                onDelete={(departmentChangeData): void => {
                  handleDeleteDepartment(departmentChangeData);
                }}
              />
            ))}
          </div>
        );
      })()}

      <CreateDepartmentModal
        showModal={showCreateDepartmentModal}
        handleClose={handleClose}
        rooms={rooms ?? []}
        handleCreate={(departmentChangeData): void => {
          handleCreateDepartment(departmentChangeData);
        }}
      />
    </div>
  );
};

export default OfficeDepartmentsGroup;
